import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { CalendarDays, Settings2, X } from 'lucide-react';
import api from '../../services/api';

/**
 * « Ma date d'écrit » — à côté de « Je prépare », en tête de thématique.
 *
 * Sans date : un bouton discret. Avec date : « Écrit le 7 déc. · J-54 » et la phase
 * de préparation (MonQuiz-book, ch. 8 et annexe C) : apprentissage jusqu'à J-30,
 * consolidation jusqu'à J-7, stabilisation ensuite. La roue rouvre le réglage :
 * une date du calendrier des concours (liée, pas recopiée : une correction du
 * calendrier suit toute seule), une date libre, ou « je ne sais pas encore ».
 * API : api/user/exam-target.php (migration 177).
 */
const DAY = 86400000;
const fmt = (iso, opts = { day: 'numeric', month: 'short' }) =>
    new Date(`${iso}T00:00:00`).toLocaleDateString('fr-FR', opts);
// Dates locales (toISOString passerait en UTC et décalerait d'un jour en France).
const isoOf = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const shift = (iso, days) => {
    const d = new Date(`${iso}T00:00:00`);
    d.setDate(d.getDate() + days);
    return isoOf(d);
};
const todayIso = () => isoOf(new Date());

// Vocabulaire : « écrit » pour les concours, « session d'examen » pour les certifications
// (thématiques communautaires : ISTQB…), où « écrit » sonne faux.
const WORDS = {
    concours: { mine: 'Ma date d’écrit', unknown: 'Date d’écrit ?', on: 'Écrit le', past: 'Écrit passé',
        hint: 'Indique ta date d’écrit', field: 'Date de ton écrit :' },
    certif: { mine: 'Ma date de session d’examen', unknown: 'Date de session d’examen ?', on: 'Examen le', past: 'Examen passé',
        hint: 'Indique ta date de session d’examen', field: 'Date de ta session d’examen :' },
};

export function examPhase(dateIso, past = WORDS.concours.past) {
    const days = Math.round((new Date(`${dateIso}T00:00:00`) - new Date(`${todayIso()}T00:00:00`)) / DAY);
    if (days < 0) return { days, label: past, hint: 'Mets à jour ta date pour la prochaine session.' };
    if (days === 0) return { days, label: 'Jour J', hint: 'Bon courage ! Rien de nouveau aujourd’hui.' };
    if (days <= 7) return { days, label: 'Stabilisation', hint: 'Révisions du jour uniquement, rien de nouveau.' };
    if (days <= 30) return { days, label: 'Consolidation', hint: `Révise et reprends tes erreurs · stabilisation dès le ${fmt(shift(dateIso, -7))}.` };
    return { days, label: 'Apprentissage', hint: `Phase d'apprentissage · consolidation dès le ${fmt(shift(dateIso, -30))}.` };
}

export default function ExamDatePill({ scopeId, parcours = '', certif = false }) {
    const w = certif ? WORDS.certif : WORDS.concours;
    const [state, setState] = useState(null); // { target, options }
    const [open, setOpen] = useState(false);
    const [custom, setCustom] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!scopeId) return;
        let alive = true;
        api.get('/user/exam-target.php', { params: { scope_id: scopeId, parcours } })
            .then(({ data }) => { if (alive) setState(data.data); })
            .catch(() => {});
        return () => { alive = false; };
    }, [scopeId, parcours]);

    useEffect(() => {
        if (!open) return;
        const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [open]);

    if (!scopeId || !state) return null;
    const { target, options = [] } = state;
    const hasDate = !!target?.date;

    const save = async (body) => {
        setSaving(true); setError(null);
        try {
            const { data } = await api.post('/user/exam-target.php', { scope_id: scopeId, parcours, ...body });
            setState(data.data);
            setOpen(false); setCustom('');
        } catch (e) {
            setError(e?.response?.data?.message || 'Enregistrement impossible, réessaie.');
        } finally { setSaving(false); }
    };

    const phase = hasDate ? examPhase(target.date, w.past) : null;

    return (
        <div className="exam-date">
            <button type="button" className={`parcours-switch__btn exam-date__btn${hasDate ? ' is-set' : ''}`}
                onClick={() => setOpen(o => !o)} aria-expanded={open}
                title={hasDate ? `${target.label}${target.previsionnel ? ' (date prévisionnelle)' : ''} — modifier` : `${w.hint} : compte à rebours et phase de préparation`}>
                <CalendarDays size={14} aria-hidden="true" />
                {hasDate
                    ? <>{w.on} {fmt(target.date)} · {phase.days > 0 ? `J-${phase.days}` : phase.label}<Settings2 size={13} aria-hidden="true" /></>
                    : (target?.unknown ? w.unknown : w.mine)}
            </button>
            {hasDate && phase.days >= 0 && (
                <span className="exam-date__phase"><strong>{phase.label}</strong> : {phase.hint}</span>
            )}

            {/* Fenêtre rendue dans <body> : l'en-tête coupe ce qui dépasse (overflow: hidden). */}
            {open && createPortal(
                <div className="exam-date__overlay" onClick={() => setOpen(false)}>
                <div className="exam-date__panel" role="dialog" aria-label={w.mine} onClick={e => e.stopPropagation()}>
                    <div className="exam-date__head">
                        <strong>{w.mine}</strong>
                        <button type="button" className="exam-date__close" onClick={() => setOpen(false)} aria-label="Fermer"><X size={16} /></button>
                    </div>
                    {options.length > 0 && (
                        <>
                            <p className="exam-date__intro">Prochains écrits du calendrier :</p>
                            <div className="exam-date__options">
                                {options.map(o => (
                                    <button key={o.id} type="button" disabled={saving}
                                        className={`exam-date__option${target?.concours_date_id === o.id ? ' is-active' : ''}`}
                                        onClick={() => save({ concours_date_id: o.id })} title={o.detail || undefined}>
                                        <span className="exam-date__option-date">{fmt(o.date, { day: 'numeric', month: 'long', year: 'numeric' })}{o.date_end && o.date_end !== o.date ? ` → ${fmt(o.date_end)}` : ''}</span>
                                        <span className="exam-date__option-label">{o.label}{o.previsionnel ? ' · prévisionnel' : ''}</span>
                                    </button>
                                ))}
                            </div>
                        </>
                    )}
                    <form className="exam-date__custom" onSubmit={(e) => { e.preventDefault(); if (custom) save({ date: custom }); }}>
                        <label htmlFor="exam-date-custom">{options.length ? 'Ou une autre date :' : w.field}</label>
                        <div className="exam-date__custom-row">
                            <input id="exam-date-custom" type="date" min={todayIso()} value={custom} onChange={e => setCustom(e.target.value)} />
                            <button type="submit" className="btn btn-primary btn-sm" disabled={!custom || saving}>Valider</button>
                        </div>
                    </form>
                    <div className="exam-date__foot">
                        {!target?.unknown && (
                            <button type="button" className="btn btn-ghost btn-sm" disabled={saving} onClick={() => save({ unknown: true })}>Je ne sais pas encore</button>
                        )}
                        {(hasDate || target?.unknown) && (
                            <button type="button" className="btn btn-ghost btn-sm" disabled={saving} onClick={() => save({ clear: true })}>Retirer</button>
                        )}
                    </div>
                    {error && <p className="exam-date__error">{error}</p>}
                </div>
                </div>,
                document.body,
            )}
        </div>
    );
}
